import type { BandTreeNode, BandTreeRecord } from './bands'
import type { ListNode } from './model'

export interface FlattenResult<T> {
  readonly nodes: readonly ListNode<T>[]
  readonly bandIds: readonly string[]
  readonly isAllCollapsed: boolean
}

export function flattenTree<T>(
  tree: ReadonlyArray<BandTreeNode<T> | BandTreeRecord<T>>,
  collapsedBandIds: ReadonlySet<string>,
): FlattenResult<T> {
  const nodes: ListNode<T>[] = []
  const bandIds: string[] = []

  for (const node of tree) {
    flattenNode(node, collapsedBandIds, nodes, bandIds, 0)
  }

  return {
    nodes,
    bandIds,
    isAllCollapsed:
      bandIds.length > 0 && bandIds.every(id => collapsedBandIds.has(id)),
  }
}

function flattenNode<T>(
  node: BandTreeNode<T> | BandTreeRecord<T>,
  collapsedBandIds: ReadonlySet<string>,
  nodes: ListNode<T>[],
  bandIds: string[],
  recordLevel: number,
): void {
  if (node.kind === 'record') {
    nodes.push({
      kind: 'record',
      id: `record:${encodeURIComponent(node.recordId)}`,
      recordId: node.recordId,
      level: recordLevel,
      record: node.record,
    })
    return
  }

  const collapsed = collapsedBandIds.has(node.id)
  bandIds.push(node.id)
  nodes.push({
    kind: 'band',
    id: node.id,
    level: node.level,
    field: node.field,
    optionId: node.optionId,
    label: node.label,
    count: node.count,
    collapsed,
    selectableIds: node.selectableIds,
  })

  if (collapsed) return

  for (const child of node.children) {
    flattenNode(child, collapsedBandIds, nodes, bandIds, node.level + 1)
  }
}
